import { warnOnce } from '@/lib/warnOnce';

/**
 * Config-driven image resolution, replacing the legacy webpack `require()` calls.
 *
 * The legacy app interpolated the config value straight into `require()`:
 *
 * ```js
 * require(`../assets/img/logos/${options.logoFile}`)      // Logo, DateWidget
 * require(`../assets/img/${item.img}`)                    // ImageLabelWidget, TravelWidget
 * require('../../assets/img/icons/' + logoName(colIndex)) // TopSpacesWidget
 * require(`@/assets/img/icons/${props.icon}-icon.svg`)    // Pin
 * ```
 *
 * Vite has no `require`, and — more importantly — webpack's `require` **throws** on a miss.
 * A single typo in a `logoFile` therefore took the whole page down, which is one of the ways
 * the legacy app white-screens. Here a miss warns once and resolves to `undefined`, and every
 * call site renders nothing rather than a broken-image icon (CLAUDE.md constraint 4).
 *
 * Note the inconsistent base directories, which are config-visible and therefore fixed:
 * `Logo`/`DateWidget` look in `img/logos/`, `ImageLabelWidget`/`TravelWidget` in `img/`, and
 * `TopSpacesWidget`/`Pin` in `img/icons/`. Use the `IMG` constants below rather than
 * open-coding them.
 */

/**
 * Every file under `src/assets/img`, eagerly, keyed by module path and valued by the emitted
 * URL. Eager because there are eleven files totalling a few hundred kilobytes and a signage
 * display must not wait on a network round trip to draw a logo it already has in its bundle.
 *
 * The pattern is **relative, not `@/`-aliased**: `import.meta.glob` resolves aliases through
 * Vite's `resolve.alias`, and this project's `@/` comes from `vite-tsconfig-paths` instead,
 * which does not participate. An aliased pattern would silently match nothing — every image
 * on every screen missing, with one warning per file.
 */
const modules = import.meta.glob<string>('../assets/img/**/*', { eager: true, import: 'default' });

const BASE = '/assets/img/';

/** `'logos/corio-logo.png'` -> the emitted URL. Built once at module evaluation. */
const urlsByPath = new Map<string, string>(
  Object.entries(modules).map(([key, url]) => [key.slice(key.indexOf(BASE) + BASE.length), url]),
);

/** The three base directories the configs assume. */
export const IMG = {
  /** `ImageLabelWidget`, `TravelWidget` — config values sit directly under `img/`. */
  root: '',
  /** `Logo`, `DateWidget`. */
  logos: 'logos/',
  /** `TopSpacesWidget`, `Pin`. */
  icons: 'icons/',
} as const;

/**
 * Resolves the first candidate path that exists under `src/assets/img`, or `undefined`.
 *
 * ```ts
 * assetUrl(`${IMG.logos}${options.logoFile}`)
 * assetUrl(`${IMG.icons}${icon}.svg`, `${IMG.icons}${icon}-icon.svg`)  // Pin, bug W-4
 * ```
 *
 * Multiple candidates exist for exactly one caller: `Pin` appended `-icon.svg` to the
 * configured icon name while the files on disk carry no such suffix, so neither spelling
 * alone covers both what is on disk today and what a config might have been written against
 * (docs/MIGRATION-WIDGETS.md §8, W-4). Every other call site passes one path.
 *
 * Warns once per candidate set — a display renders for weeks and a missing asset is a
 * permanent property of the config, so warning per render would bury the console.
 */
export function assetUrl(...candidates: string[]): string | undefined {
  for (const candidate of candidates) {
    const url = urlsByPath.get(normalise(candidate));
    if (url !== undefined) return url;
  }

  warnOnce(
    `asset-missing:${candidates.join('|')}`,
    '[signage] image not found in src/assets/img: %s. Rendering nothing for it — check the ' +
      '`logoFile` / `img` / `icon` values in this signage config.',
    candidates.join(' or '),
  );
  return undefined;
}

/** Tolerates the `./foo.png` and `/foo.png` spellings a hand-edited config might carry. */
function normalise(path: string): string {
  return path.replace(/^\.?\//, '');
}
